import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import api from '../../utils/api';
import { API_BASE_URL, API_ENDPOINTS } from '../../config';
import { track, setUser } from '../../utils/analytics';
import '../../styles/auth.css';

interface LoginForm {
  email: string;
  password: string;
}

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    document.title = 'OfferFlow | Sign In';
  }, []);

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const userUid = localStorage.getItem('user_uid');
        if (!token || !userUid) return;

        await api.get(API_ENDPOINTS.me);
        navigate('/welcome', { replace: true });
      } catch {
      }
    };

    checkExistingSession();
  }, [navigate]);

  useEffect(() => {
    if (location.state?.message && location.state?.type === 'success') {
      setSuccessMessage(location.state.message);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (tokenResponse) => handleGoogleSuccess(tokenResponse.access_token),
    onError: () => setError('Google sign-in failed'),
  });

  const handleGoogleSuccess = async (access_token: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token }),
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('user_uid', data.user_uid);
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user', JSON.stringify({ uid: data.user_uid }));

        setUser(data.user_uid);
        track('login', { method: 'google' });
        window.dispatchEvent(new Event('auth-token-set'));
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Google sign-in failed');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/v1/login`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: '',
          username: formData.email,
          password: formData.password,
          scope: '',
          client_id: '',
          client_secret: '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        localStorage.setItem('user_uid', data.user_uid);
        localStorage.setItem('access_token', data.access_token);

        const userData = {
          uid: data.user_uid,
          email: formData.email,
          first_name: '',
          last_name: ''
        };
        localStorage.setItem('user', JSON.stringify(userData));

        setUser(data.user_uid);
        track('login', { method: 'email' });

        window.dispatchEvent(new Event('auth-token-set'));

        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-left">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Sign in to OfferFlow</h1>
            <p>Focused workspace for managing your job search.</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
              />
            </div>

            <button 
              type="submit" 
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="auth-divider"><span>or</span></div>

          <div className="auth-google-wrapper">
            <button
              type="button"
              className="auth-button-google"
              onClick={() => loginWithGoogle()}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <div className="auth-footer">
            <p>
              <Link to="/restore" className="auth-link">
                Forgot your password?
              </Link>
            </p>
            <p className="auth-footer-row">
              <span>Don't have an account?</span>
              <Link to="/sign-up" className="auth-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <div className="auth-right">
        <div className="auth-visual auth-visual--signin">
          <div className="auth-visual-icon">
            <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="60" cy="60" r="50" stroke="#d1d5db" strokeWidth="2" strokeDasharray="4 4"/>
              <path d="M40 60 L55 75 L80 45" stroke="#111827" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="30" y="30" width="60" height="60" rx="8" stroke="#e5e7eb" strokeWidth="1.5" fill="none"/>
              <path d="M50 50 L70 50 M50 60 L70 60 M50 70 L65 70" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="auth-visual-text">
            <h3>Welcome Back</h3>
            <p>Access your job search workspace</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
